/**
 * Cross-client game preferences: expansions/modules, reading progress, checklists,
 * first-game / guided steps, variant-scoped progress.
 * Pure data + immutable merges — usable by PWA and Expo.
 */

export const GAME_PREFS_SCHEMA_VERSION = 3;
/** Current web root key. */
export const GAME_PREFS_STORAGE_KEY = 'planszowki.pwa.v3';
/** Previous web root key (v2). */
export const LEGACY_PWA_V2_KEY = 'planszowki.pwa.v2';
/** Legacy PWA blob (checklists / chapterIndex scroll). */
export const LEGACY_PWA_V1_KEY = 'planszowki.pwa.v1';

export type VariantProgressSlice = {
  readSectionIds: string[];
  completedChecklistIds: string[];
  lastSectionId: string | null;
  firstGameStepIndex: number;
  firstGameCompletedStepIds: string[];
  guidedStepId: string | null;
};

export type GamePrefs = {
  selectedExpansionIds: string[];
  selectedModuleIds: string[];
  activeVariantId: string | null;
  readSectionIds: string[];
  completedChecklistIds: string[];
  lastSectionId: string | null;
  firstGameStepIndex: number;
  firstGameCompletedStepIds: string[];
  guidedStepId: string | null;
  guidedCompletedChecklistIds: string[];
  readingBarExpanded: boolean;
  readingBarHidden: boolean;
  selectedScenarioIds: Record<string, string | null>;
  lastAlienSectionId: string | null;
  /** Independent progress per variant (e.g. Vinhos). */
  variantProgress: Record<string, VariantProgressSlice>;
};

export type RootGamePrefs = {
  version: typeof GAME_PREFS_SCHEMA_VERSION;
  games: Record<string, GamePrefs>;
  lastGameId: string | null;
  dismissedUpdatePromptAt: string | null;
};

export function createDefaultVariantSlice(): VariantProgressSlice {
  return {
    readSectionIds: [],
    completedChecklistIds: [],
    lastSectionId: null,
    firstGameStepIndex: 0,
    firstGameCompletedStepIds: [],
    guidedStepId: null,
  };
}

export function createDefaultGamePrefs(): GamePrefs {
  return {
    selectedExpansionIds: [],
    selectedModuleIds: [],
    activeVariantId: null,
    readSectionIds: [],
    completedChecklistIds: [],
    lastSectionId: null,
    firstGameStepIndex: 0,
    firstGameCompletedStepIds: [],
    guidedStepId: null,
    guidedCompletedChecklistIds: [],
    readingBarExpanded: false,
    readingBarHidden: false,
    selectedScenarioIds: {},
    lastAlienSectionId: null,
    variantProgress: {},
  };
}

export function createDefaultRootPrefs(): RootGamePrefs {
  return {
    version: GAME_PREFS_SCHEMA_VERSION,
    games: {},
    lastGameId: null,
    dismissedUpdatePromptAt: null,
  };
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

function asScenarioMap(value: unknown): Record<string, string | null> {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, string | null> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    out[key] = typeof entry === 'string' ? entry : null;
  }
  return out;
}

function normalizeVariantSlice(raw: unknown): VariantProgressSlice {
  const base = createDefaultVariantSlice();
  if (!raw || typeof raw !== 'object') return base;
  const value = raw as Partial<VariantProgressSlice>;
  return {
    readSectionIds: asStringArray(value.readSectionIds),
    completedChecklistIds: asStringArray(value.completedChecklistIds),
    lastSectionId:
      typeof value.lastSectionId === 'string' ? value.lastSectionId : null,
    firstGameStepIndex:
      typeof value.firstGameStepIndex === 'number' && value.firstGameStepIndex >= 0
        ? Math.floor(value.firstGameStepIndex)
        : 0,
    firstGameCompletedStepIds: asStringArray(value.firstGameCompletedStepIds),
    guidedStepId:
      typeof value.guidedStepId === 'string' ? value.guidedStepId : null,
  };
}

function normalizeGamePrefs(raw: unknown): GamePrefs {
  const base = createDefaultGamePrefs();
  if (!raw || typeof raw !== 'object') return base;
  const value = raw as Partial<GamePrefs>;
  const variantProgress: Record<string, VariantProgressSlice> = {};
  if (value.variantProgress && typeof value.variantProgress === 'object') {
    for (const [id, slice] of Object.entries(value.variantProgress)) {
      variantProgress[id] = normalizeVariantSlice(slice);
    }
  }
  return {
    selectedExpansionIds: asStringArray(value.selectedExpansionIds),
    selectedModuleIds: asStringArray(value.selectedModuleIds),
    activeVariantId:
      typeof value.activeVariantId === 'string' ? value.activeVariantId : null,
    readSectionIds: asStringArray(value.readSectionIds),
    completedChecklistIds: asStringArray(value.completedChecklistIds),
    lastSectionId:
      typeof value.lastSectionId === 'string' ? value.lastSectionId : null,
    firstGameStepIndex:
      typeof value.firstGameStepIndex === 'number' && value.firstGameStepIndex >= 0
        ? Math.floor(value.firstGameStepIndex)
        : 0,
    firstGameCompletedStepIds: asStringArray(value.firstGameCompletedStepIds),
    guidedStepId:
      typeof value.guidedStepId === 'string' ? value.guidedStepId : null,
    guidedCompletedChecklistIds: asStringArray(
      value.guidedCompletedChecklistIds
    ),
    readingBarExpanded: value.readingBarExpanded === true,
    readingBarHidden: value.readingBarHidden === true,
    selectedScenarioIds: asScenarioMap(value.selectedScenarioIds),
    lastAlienSectionId:
      typeof value.lastAlienSectionId === 'string'
        ? value.lastAlienSectionId
        : null,
    variantProgress,
  };
}

/** Immutable per-game field merge — never wipe sibling fields. */
export function mergeGamePrefs(
  current: GamePrefs | undefined,
  patch: Partial<GamePrefs>
): GamePrefs {
  const base = current ?? createDefaultGamePrefs();
  return {
    selectedExpansionIds:
      patch.selectedExpansionIds !== undefined
        ? [...patch.selectedExpansionIds]
        : [...base.selectedExpansionIds],
    selectedModuleIds:
      patch.selectedModuleIds !== undefined
        ? [...patch.selectedModuleIds]
        : [...base.selectedModuleIds],
    activeVariantId:
      patch.activeVariantId !== undefined
        ? patch.activeVariantId
        : base.activeVariantId,
    readSectionIds:
      patch.readSectionIds !== undefined
        ? [...patch.readSectionIds]
        : [...base.readSectionIds],
    completedChecklistIds:
      patch.completedChecklistIds !== undefined
        ? [...patch.completedChecklistIds]
        : [...base.completedChecklistIds],
    lastSectionId:
      patch.lastSectionId !== undefined
        ? patch.lastSectionId
        : base.lastSectionId,
    firstGameStepIndex:
      patch.firstGameStepIndex !== undefined
        ? patch.firstGameStepIndex
        : base.firstGameStepIndex,
    firstGameCompletedStepIds:
      patch.firstGameCompletedStepIds !== undefined
        ? [...patch.firstGameCompletedStepIds]
        : [...base.firstGameCompletedStepIds],
    guidedStepId:
      patch.guidedStepId !== undefined ? patch.guidedStepId : base.guidedStepId,
    guidedCompletedChecklistIds:
      patch.guidedCompletedChecklistIds !== undefined
        ? [...patch.guidedCompletedChecklistIds]
        : [...base.guidedCompletedChecklistIds],
    readingBarExpanded:
      patch.readingBarExpanded !== undefined
        ? patch.readingBarExpanded
        : base.readingBarExpanded,
    readingBarHidden:
      patch.readingBarHidden !== undefined
        ? patch.readingBarHidden
        : base.readingBarHidden,
    selectedScenarioIds:
      patch.selectedScenarioIds !== undefined
        ? { ...patch.selectedScenarioIds }
        : { ...base.selectedScenarioIds },
    lastAlienSectionId:
      patch.lastAlienSectionId !== undefined
        ? patch.lastAlienSectionId
        : base.lastAlienSectionId,
    variantProgress:
      patch.variantProgress !== undefined
        ? { ...patch.variantProgress }
        : { ...base.variantProgress },
  };
}

export function mergeRootPrefs(
  current: RootGamePrefs,
  patch: {
    lastGameId?: string | null;
    dismissedUpdatePromptAt?: string | null;
    gameId?: string;
    gamePatch?: Partial<GamePrefs>;
  }
): RootGamePrefs {
  const games = { ...current.games };
  if (patch.gameId && patch.gamePatch) {
    games[patch.gameId] = mergeGamePrefs(games[patch.gameId], patch.gamePatch);
  }
  return {
    version: GAME_PREFS_SCHEMA_VERSION,
    games,
    lastGameId:
      patch.lastGameId !== undefined ? patch.lastGameId : current.lastGameId,
    dismissedUpdatePromptAt:
      patch.dismissedUpdatePromptAt !== undefined
        ? patch.dismissedUpdatePromptAt
        : current.dismissedUpdatePromptAt,
  };
}

export function getGamePrefs(
  root: RootGamePrefs,
  gameId: string
): GamePrefs {
  return root.games[gameId] ?? createDefaultGamePrefs();
}

/**
 * When a game uses independent variant progress (Vinhos), overlay the active
 * variant slice onto the live reading/first-game fields.
 */
export function applyActiveVariantProgress(prefs: GamePrefs): GamePrefs {
  if (!prefs.activeVariantId) return prefs;
  const slice = prefs.variantProgress[prefs.activeVariantId];
  if (!slice) return prefs;
  return mergeGamePrefs(prefs, {
    readSectionIds: slice.readSectionIds,
    completedChecklistIds: slice.completedChecklistIds,
    lastSectionId: slice.lastSectionId,
    firstGameStepIndex: slice.firstGameStepIndex,
    firstGameCompletedStepIds: slice.firstGameCompletedStepIds,
    guidedStepId: slice.guidedStepId,
  });
}

/** Persist current live progress into the active variant bucket. */
export function snapshotToActiveVariant(prefs: GamePrefs): GamePrefs {
  if (!prefs.activeVariantId) return prefs;
  return mergeGamePrefs(prefs, {
    variantProgress: {
      ...prefs.variantProgress,
      [prefs.activeVariantId]: {
        readSectionIds: [...prefs.readSectionIds],
        completedChecklistIds: [...prefs.completedChecklistIds],
        lastSectionId: prefs.lastSectionId,
        firstGameStepIndex: prefs.firstGameStepIndex,
        firstGameCompletedStepIds: [...prefs.firstGameCompletedStepIds],
        guidedStepId: prefs.guidedStepId,
      },
    },
  });
}

/** Switch variant: snapshot current, load target slice (or empty). */
export function switchActiveVariant(
  prefs: GamePrefs,
  nextVariantId: string | null
): GamePrefs {
  const snapshotted = snapshotToActiveVariant(prefs);
  if (!nextVariantId) {
    return mergeGamePrefs(snapshotted, { activeVariantId: null });
  }
  const slice =
    snapshotted.variantProgress[nextVariantId] ?? createDefaultVariantSlice();
  return mergeGamePrefs(snapshotted, {
    activeVariantId: nextVariantId,
    readSectionIds: slice.readSectionIds,
    completedChecklistIds: slice.completedChecklistIds,
    lastSectionId: slice.lastSectionId,
    firstGameStepIndex: slice.firstGameStepIndex,
    firstGameCompletedStepIds: slice.firstGameCompletedStepIds,
    guidedStepId: slice.guidedStepId,
  });
}

export function toggleIdInList(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id];
}

export function markSectionRead(prefs: GamePrefs, sectionId: string): GamePrefs {
  const readSectionIds = prefs.readSectionIds.includes(sectionId)
    ? prefs.readSectionIds
    : [...prefs.readSectionIds, sectionId];
  return mergeGamePrefs(prefs, {
    readSectionIds,
    lastSectionId: sectionId,
  });
}

export function unmarkSectionRead(
  prefs: GamePrefs,
  sectionId: string
): GamePrefs {
  return mergeGamePrefs(prefs, {
    readSectionIds: prefs.readSectionIds.filter((id) => id !== sectionId),
  });
}

export function toggleSectionRead(
  prefs: GamePrefs,
  sectionId: string
): GamePrefs {
  return prefs.readSectionIds.includes(sectionId)
    ? unmarkSectionRead(prefs, sectionId)
    : markSectionRead(prefs, sectionId);
}

export function calculateReadingPercent(
  readSectionIds: string[],
  totalSections: number
): number {
  if (totalSections <= 0) return 0;
  const unique = new Set(readSectionIds);
  return Math.min(
    100,
    Math.round((unique.size / totalSections) * 100)
  );
}

export function sectionIdForChapter(chapterIndex: number): string {
  return `chapter:${chapterIndex}`;
}

export function chapterIndexFromSectionId(sectionId: string | null): number | null {
  if (!sectionId) return null;
  const match = /^chapter:(\d+)$/.exec(sectionId);
  if (!match) return null;
  return Number(match[1]);
}

export function toggleChecklistItem(
  prefs: GamePrefs,
  itemId: string,
  checked: boolean
): GamePrefs {
  const set = new Set(prefs.completedChecklistIds);
  if (checked) set.add(itemId);
  else set.delete(itemId);
  return mergeGamePrefs(prefs, {
    completedChecklistIds: Array.from(set),
  });
}

export function toggleExpansionId(
  prefs: GamePrefs,
  expansionId: string,
  enabled: boolean
): GamePrefs {
  const set = new Set(prefs.selectedExpansionIds);
  if (enabled) set.add(expansionId);
  else set.delete(expansionId);
  return mergeGamePrefs(prefs, {
    selectedExpansionIds: Array.from(set),
  });
}

export function toggleModuleId(
  prefs: GamePrefs,
  moduleId: string,
  enabled: boolean,
  options: { alwaysOnIds?: string[] } = {}
): GamePrefs {
  const alwaysOn = new Set(options.alwaysOnIds ?? []);
  if (alwaysOn.has(moduleId) && !enabled) {
    return prefs;
  }
  const set = new Set(prefs.selectedModuleIds);
  if (enabled) set.add(moduleId);
  else set.delete(moduleId);
  for (const id of alwaysOn) set.add(id);
  return mergeGamePrefs(prefs, {
    selectedModuleIds: Array.from(set),
  });
}

export function setFirstGameStepIndex(
  prefs: GamePrefs,
  index: number
): GamePrefs {
  return mergeGamePrefs(prefs, {
    firstGameStepIndex: Math.max(0, Math.floor(index)),
  });
}

export function toggleFirstGameStepCompleted(
  prefs: GamePrefs,
  stepId: string,
  completed: boolean
): GamePrefs {
  const set = new Set(prefs.firstGameCompletedStepIds);
  if (completed) set.add(stepId);
  else set.delete(stepId);
  return mergeGamePrefs(prefs, {
    firstGameCompletedStepIds: Array.from(set),
  });
}

export function setGuidedStepId(
  prefs: GamePrefs,
  stepId: string | null
): GamePrefs {
  return mergeGamePrefs(prefs, { guidedStepId: stepId });
}

type LegacyV1 = {
  schemaVersion?: number;
  checklists?: Record<string, Record<string, boolean>>;
  reading?: Record<
    string,
    { chapterIndex?: number; scrollRatio?: number; updatedAt?: string }
  >;
  lastGameId?: string | null;
  dismissedUpdatePromptAt?: string | null;
};

/** Migrate broken/legacy `planszowki.pwa.v1` into versioned root prefs. */
export function migrateFromLegacyV1(raw: unknown): RootGamePrefs {
  const root = createDefaultRootPrefs();
  if (!raw || typeof raw !== 'object') return root;
  const legacy = raw as LegacyV1;
  root.lastGameId = legacy.lastGameId ?? null;
  root.dismissedUpdatePromptAt = legacy.dismissedUpdatePromptAt ?? null;

  const gameIds = new Set<string>([
    ...Object.keys(legacy.checklists ?? {}),
    ...Object.keys(legacy.reading ?? {}),
  ]);

  for (const gameId of gameIds) {
    const checklistMap = legacy.checklists?.[gameId] ?? {};
    const completedChecklistIds = Object.entries(checklistMap)
      .filter(([, checked]) => checked)
      .map(([id]) => id);
    const reading = legacy.reading?.[gameId];
    const chapterIndex =
      typeof reading?.chapterIndex === 'number' ? reading.chapterIndex : null;
    const lastSectionId =
      chapterIndex != null ? sectionIdForChapter(chapterIndex) : null;
    const readSectionIds =
      chapterIndex != null && chapterIndex >= 0
        ? Array.from({ length: chapterIndex }, (_, index) =>
            sectionIdForChapter(index)
          )
        : [];

    root.games[gameId] = mergeGamePrefs(undefined, {
      completedChecklistIds,
      lastSectionId,
      readSectionIds,
    });
  }

  return root;
}

export function parseRootPrefs(raw: unknown): RootGamePrefs {
  if (!raw || typeof raw !== 'object') {
    return createDefaultRootPrefs();
  }
  const value = raw as Partial<RootGamePrefs> & { schemaVersion?: number };
  const version =
    typeof value.version === 'number'
      ? value.version
      : typeof value.schemaVersion === 'number'
        ? value.schemaVersion
        : 0;

  if (version < 2 && ('checklists' in value || 'reading' in value)) {
    return migrateFromLegacyV1(value);
  }

  const games: Record<string, GamePrefs> = {};
  if (value.games && typeof value.games === 'object') {
    for (const [gameId, prefs] of Object.entries(value.games)) {
      games[gameId] = normalizeGamePrefs(prefs);
    }
  }

  return {
    version: GAME_PREFS_SCHEMA_VERSION,
    games,
    lastGameId: typeof value.lastGameId === 'string' ? value.lastGameId : null,
    dismissedUpdatePromptAt:
      typeof value.dismissedUpdatePromptAt === 'string'
        ? value.dismissedUpdatePromptAt
        : null,
  };
}
